import type { ChordAnchor, Line, Occurrence, RevisionContent, Section } from '../schema/v1'
import { validateContent } from '../validate'

/**
 * Reconciliación de IDs al editar (ADR 0003, punto 5): una revisión hija conserva los IDs de lo
 * que no cambia, para que el mapa de tiempos de E2 siga apuntando a los mismos acordes.
 *
 * Cuando editas el texto de una ficha, el parser vuelve a numerar todo desde cero (`s1`, `l1`,
 * `c1`…). Aquí se empareja lo nuevo con lo anterior y se devuelven los IDs viejos a lo que sigue
 * siendo lo mismo. Los IDs de lo que se añade son siempre nuevos: nunca reutilizan el de algo
 * que se borró, porque una revisión antigua todavía puede apuntar a él.
 */
type Equal<T> = (a: T, b: T) => boolean

/** Por encima de este tamaño no se usa la tabla de programación dinámica (memoria). */
const MAX_TABLE = 1_000_000

/** Subsecuencia común más larga de dos listas de índices. Devuelve pares (a, b) en orden. */
function longestCommon(
  a: readonly number[],
  b: readonly number[],
  equal: (a: number, b: number) => boolean,
): [number, number][] {
  if (a.length === 0 || b.length === 0) return []
  if (a.length * b.length > MAX_TABLE) {
    // Demasiado grande para la tabla: emparejamiento voraz en orden.
    const pairs: [number, number][] = []
    let from = 0
    for (const bi of b) {
      for (let k = from; k < a.length; k += 1) {
        if (equal(a[k]!, bi)) {
          pairs.push([a[k]!, bi])
          from = k + 1
          break
        }
      }
    }
    return pairs
  }
  const rows = a.length + 1
  const cols = b.length + 1
  const table: number[] = new Array<number>(rows * cols).fill(0)
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i * cols + j] = equal(a[i]!, b[j]!)
        ? table[(i + 1) * cols + j + 1]! + 1
        : Math.max(table[(i + 1) * cols + j]!, table[i * cols + j + 1]!)
    }
  }
  const pairs: [number, number][] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (equal(a[i]!, b[j]!)) {
      pairs.push([a[i]!, b[j]!])
      i += 1
      j += 1
    } else if (table[(i + 1) * cols + j]! >= table[i * cols + j + 1]!) {
      i += 1
    } else {
      j += 1
    }
  }
  return pairs
}

interface Gap {
  a: number[]
  b: number[]
}

/**
 * Huecos entre emparejamientos ya hechos: lo que queda sin emparejar entre dos anclas
 * consecutivas, en cada lado. Las parejas que se movieron de sitio no cuentan como anclas.
 */
function gapsBetween(
  match: readonly number[],
  taken: readonly boolean[],
  anchored: readonly boolean[],
): Gap[] {
  const anchors: [number, number][] = []
  let lastA = -1
  match.forEach((ai, bi) => {
    if (ai >= 0 && anchored[bi] && ai > lastA) {
      anchors.push([ai, bi])
      lastA = ai
    }
  })
  anchors.push([taken.length, match.length])
  const gaps: Gap[] = []
  let previousA = -1
  let previousB = -1
  for (const [anchorA, anchorB] of anchors) {
    const gap: Gap = { a: [], b: [] }
    for (let ai = previousA + 1; ai < anchorA; ai += 1) if (!taken[ai]) gap.a.push(ai)
    for (let bi = previousB + 1; bi < anchorB; bi += 1) if (match[bi] === -1) gap.b.push(bi)
    if (gap.a.length > 0 && gap.b.length > 0) gaps.push(gap)
    previousA = anchorA
    previousB = anchorB
  }
  return gaps
}

interface MatchOptions {
  /** Emparejar también lo idéntico que cambió de sitio. */
  moves: boolean
}

/**
 * Empareja dos listas: primero lo idéntico en orden, después, dentro de cada hueco, lo que se
 * parece cada vez menos (`levels`), y por último por posición cuando el hueco tiene el mismo
 * tamaño a ambos lados. Devuelve, para cada elemento de `b`, el índice de `a` o −1.
 */
function matchSequences<T>(
  a: readonly T[],
  b: readonly T[],
  levels: readonly Equal<T>[],
  options: MatchOptions,
): number[] {
  const match: number[] = new Array<number>(b.length).fill(-1)
  const taken: boolean[] = new Array<boolean>(a.length).fill(false)
  const anchored: boolean[] = new Array<boolean>(b.length).fill(false)
  const link = (ai: number, bi: number, anchor = true) => {
    match[bi] = ai
    taken[ai] = true
    anchored[bi] = anchor
  }
  const indices = (length: number) => Array.from({ length }, (_, index) => index)

  levels.forEach((equal, level) => {
    const same = (ai: number, bi: number) => equal(a[ai]!, b[bi]!)
    if (level === 0) {
      for (const [ai, bi] of longestCommon(indices(a.length), indices(b.length), same)) link(ai, bi)
      if (options.moves) {
        for (let bi = 0; bi < b.length; bi += 1) {
          if (match[bi] !== -1) continue
          const ai = indices(a.length).find((candidate) => !taken[candidate] && same(candidate, bi))
          if (ai !== undefined) link(ai, bi, false)
        }
      }
    } else {
      for (const gap of gapsBetween(match, taken, anchored)) {
        for (const [ai, bi] of longestCommon(gap.a, gap.b, same)) link(ai, bi)
      }
    }
  })

  for (const gap of gapsBetween(match, taken, anchored)) {
    if (gap.a.length === gap.b.length) gap.a.forEach((ai, k) => link(ai, gap.b[k]!))
  }
  return match
}

const lineText = (line: Line) => `${line.kind ?? 'lyrics'}\n${line.text}`
const lineChords = (line: Line) => line.chords.map((anchor) => anchor.chord).join(' ')
const lineSignature = (line: Line) =>
  `${lineText(line)}\n${line.chords.map((anchor) => `${anchor.chord}@${anchor.at}`).join(' ')}`

const lineLevels: Equal<Line>[] = [
  (x, y) => lineSignature(x) === lineSignature(y),
  (x, y) => lineText(x) === lineText(y),
  (x, y) =>
    x.chords.length > 0 &&
    (x.kind ?? 'lyrics') === (y.kind ?? 'lyrics') &&
    lineChords(x) === lineChords(y),
]

const anchorLevels: Equal<ChordAnchor>[] = [
  (x, y) => x.chord === y.chord && x.at === y.at,
  (x, y) => x.chord === y.chord,
]

/** Generador de IDs nuevos que parte por encima de todos los numerados que ya existían. */
export function freshIds(previous: RevisionContent): (prefix: string) => string {
  const highest = new Map<string, number>()
  const note = (id: string) => {
    const parts = /^(.*?)(\d+)$/.exec(id)
    if (!parts) return
    const prefix = parts[1]!
    highest.set(prefix, Math.max(highest.get(prefix) ?? 0, Number(parts[2])))
  }
  for (const section of previous.sections) {
    note(section.id)
    for (const line of section.lines) {
      note(line.id)
      for (const anchor of line.chords) note(anchor.id)
    }
  }
  for (const occurrence of previous.sequence) note(occurrence.id)
  return (prefix) => {
    const value = (highest.get(prefix) ?? 0) + 1
    highest.set(prefix, value)
    return `${prefix}${value}`
  }
}

interface Flat {
  section: number
  line: Line
}

function flatten(content: RevisionContent): Flat[] {
  return content.sections.flatMap((section, index) =>
    section.lines.map((line) => ({ section: index, line })),
  )
}

/**
 * Devuelve `next` con los IDs de `previous` allí donde lo anterior y lo nuevo son lo mismo.
 * `next` solo aporta la estructura (secciones, líneas, acordes y secuencia).
 */
export function reconcileIds(previous: RevisionContent, next: RevisionContent): RevisionContent {
  const fresh = freshIds(previous)
  const before = flatten(previous)
  const after = flatten(next)

  const lineMatch = matchSequences(
    before.map((entry) => entry.line),
    after.map((entry) => entry.line),
    lineLevels,
    { moves: true },
  )

  // Cada sección nueva hereda el ID de la sección anterior de la que más líneas vienen.
  const votes: [number, number, number][] = []
  const counts = new Map<string, number>()
  after.forEach((entry, index) => {
    const origin = lineMatch[index]!
    if (origin < 0) return
    const key = `${entry.section}:${before[origin]!.section}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  })
  for (const [key, count] of counts) {
    const [nextIndex, previousIndex] = key.split(':').map(Number) as [number, number]
    votes.push([count, nextIndex, previousIndex])
  }
  votes.sort((x, y) => y[0] - x[0] || x[1] - y[1] || x[2] - y[2])

  const sectionOrigin: number[] = new Array<number>(next.sections.length).fill(-1)
  const claimed = new Set<number>()
  for (const [, nextIndex, previousIndex] of votes) {
    if (sectionOrigin[nextIndex] !== -1 || claimed.has(previousIndex)) continue
    sectionOrigin[nextIndex] = previousIndex
    claimed.add(previousIndex)
  }
  // Las secciones sin líneas emparejadas (vacías o reescritas del todo) se buscan por nombre.
  next.sections.forEach((section, nextIndex) => {
    if (sectionOrigin[nextIndex] !== -1) return
    const candidate = previous.sections.findIndex(
      (other, index) =>
        !claimed.has(index) && other.kind === section.kind && other.label === section.label,
    )
    if (candidate >= 0) {
      sectionOrigin[nextIndex] = candidate
      claimed.add(candidate)
    }
  })

  const sectionIds = next.sections.map((_, index) =>
    sectionOrigin[index]! >= 0 ? previous.sections[sectionOrigin[index]!]!.id : fresh('s'),
  )

  let offset = 0
  const sections: Section[] = next.sections.map((section, sectionIndex) => {
    const lines: Line[] = section.lines.map((line, lineIndex) => {
      const flatIndex = offset + lineIndex
      const origin = lineMatch[flatIndex]!
      if (origin < 0) {
        return {
          ...line,
          id: fresh('l'),
          chords: line.chords.map((a) => ({ ...a, id: fresh('c') })),
        }
      }
      const old = before[origin]!.line
      const anchorMatch = matchSequences(old.chords, line.chords, anchorLevels, { moves: false })
      return {
        ...line,
        id: old.id,
        chords: line.chords.map((anchor, index) => ({
          ...anchor,
          id: anchorMatch[index]! >= 0 ? old.chords[anchorMatch[index]!]!.id : fresh('c'),
        })),
      }
    })
    offset += section.lines.length
    return { ...section, id: sectionIds[sectionIndex]!, lines }
  })

  // Las apariciones: la n-ésima de una sección conserva el ID de la n-ésima de la anterior.
  const previousOccurrences = new Map<string, string[]>()
  for (const occurrence of previous.sequence) {
    const list = previousOccurrences.get(occurrence.section) ?? []
    list.push(occurrence.id)
    previousOccurrences.set(occurrence.section, list)
  }
  const sectionIndexById = new Map(next.sections.map((section, index) => [section.id, index]))
  const used = new Map<string, number>()
  const sequence: Occurrence[] = next.sequence.map((occurrence) => {
    const index = sectionIndexById.get(occurrence.section)
    if (index === undefined) return { id: fresh('o'), section: occurrence.section }
    const count = used.get(occurrence.section) ?? 0
    used.set(occurrence.section, count + 1)
    const origin = sectionOrigin[index]!
    const reused =
      origin >= 0 ? previousOccurrences.get(previous.sections[origin]!.id)?.[count] : undefined
    return { id: reused ?? fresh('o'), section: sectionIds[index]! }
  })

  const result: RevisionContent = { ...next, sections, sequence }
  result.beatMap = carryBeatMap(previous, result)
  return result
}

/**
 * Conserva del mapa de tiempos lo que sigue apuntando a algo que existe. Si lo que queda no es
 * coherente, o ya no hay nada, el mapa vuelve a «ausente». Si quedó incompleto, deja de ser
 * «revisado»: cambió la ficha y hay que volver a mirarlo.
 */
function carryBeatMap(
  previous: RevisionContent,
  next: RevisionContent,
): RevisionContent['beatMap'] {
  const { beatMap } = previous
  if (beatMap.status === 'absent') return { status: 'absent', events: [] }

  const chordsByOccurrence = new Map<string, Set<string>>()
  const sectionById = new Map(next.sections.map((section) => [section.id, section]))
  let expected = 0
  for (const occurrence of next.sequence) {
    const section = sectionById.get(occurrence.section)
    const ids = new Set(section?.lines.flatMap((line) => line.chords.map((anchor) => anchor.id)))
    chordsByOccurrence.set(occurrence.id, ids)
    expected += ids.size
  }
  const events = beatMap.events.filter((event) => {
    const chords = chordsByOccurrence.get(event.occurrence)
    return chords !== undefined && (event.kind === 'rest' || chords.has(event.chord))
  })
  const candidate: RevisionContent = { ...next, beatMap: { ...beatMap, events } }
  const broken = validateContent(candidate).some((issue) => issue.code.startsWith('beatmap.'))
  if (events.length === 0 || broken) return { status: 'absent', events: [] }

  const covered = events.filter((event) => event.kind === 'chord').length
  const complete = covered === expected && events.length === beatMap.events.length
  return {
    ...beatMap,
    status: complete
      ? beatMap.status
      : beatMap.status === 'reviewed'
        ? 'estimated'
        : beatMap.status,
    events,
  }
}
